import Icon from '@components/Icon';
import Typography from '@components/Typography';
import { cssJoin } from '@shared/util';
// biome-ignore lint/style/noRestrictedImports: wrapper module re-exports
import NextLink, { type LinkProps as NextLinkProps } from 'next/link';
import { forwardRef, type ReactNode } from 'react';

interface LinkProps extends Omit<NextLinkProps, 'href'> {
  href?: string;
  color?: Color;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  fontSize?: 'light' | 'medium';
  type?: 'external' | 'internal';
  variant?: 'plain' | 'inline' | 'underlined';
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      as,
      href,
      disabled,
      children,
      className,
      type = 'internal',
      color = 'primary',
      variant = 'plain',
      fontSize = 'medium',
      ...restProps
    },
    ref,
  ) => {
    if (!href) {
      return (
        <Typography
          ref={ref}
          color={color}
          component="button"
          variant="Body 14 - Emphasis"
          {...restProps}
          className={cssJoin(
            className,
            variant === 'plain' && 'link-plain',
            variant === 'inline' && 'link-inline',
            variant === 'underlined' && 'link-underlined',
          )}
        >
          {children}
        </Typography>
      );
    }

    if (type === 'external') {
      return (
        <a
          ref={ref}
          href={href}
          target="_blank"
          {...restProps}
          aria-disabled={disabled}
          rel="noopener noreferrer"
          className={cssJoin(
            'flex',
            'gap-8',
            className,
            'items-center',
            `link-${variant}`,
            `font-${fontSize}`,
            color && `text-${color}`,
          )}
        >
          {children}
          <Icon icon="external-link" />
        </a>
      );
    }

    return (
      <NextLink
        as={as}
        ref={ref}
        href={href}
        {...restProps}
        aria-disabled={disabled}
        className={cssJoin(
          'w-fit',
          className,
          `link-${variant}`,
          `font-${fontSize}`,
          color && `text-${color}`,
        )}
      >
        {children}
      </NextLink>
    );
  },
);

Link.displayName = 'Link';

export default Link;
